<template>
  <BaseDialog
    centered
    contentClass="edit-job-location"
    ref="dialog"
    show-actions
    :persistent="true"
    :on-close="cancelClick"
  >
    <template slot="title">{{ $t('page.opportunity-details.opportunity-manipulations.edit-job-location') }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <div class="edit-job-location__search-box">
          <FormMapSearchBox
            ref="jobLocationInput"
            :label="$t('page.opportunities.data-table-titles.job-location')"
            is-required
            v-model="value"
          />
        </div>
        <div v-if="singleTenderDelivery" class="edit-job-location__delivery-area">
          <FormButton is-outlined :disabled="loading" @click.native.prevent="onUseTenderDelivery">
            {{ $t('page.opportunity-details.opportunity-manipulations.use-tender-delivery-location') }}
          </FormButton>
          <p>
            {{
              $t(
                `page.opportunity-details.opportunity-manipulations.${
                  unresolvedJobLocation ? 'tender-delivery-estimate-help' : 'tender-delivery-replace-help'
                }`,
              )
            }}
          </p>
        </div>
        <div class="edit-job-location__actions">
          <FormButton is-outlined class="edit-job-location__cancel" @click.native="cancelClick" :disabled="loading">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton
            @click.native="onSubmit"
            type="submit"
            :disabled="isFormInvalid || !hasChanged"
            :is-loading="loading"
          >
            <span>{{ $t('actions.save') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import FormButton from '@/components/form/FormButton';
import { mapActions } from 'vuex';
import FormMapSearchBox from '@/components/form/FormMapSearchBox.vue';
import { isCanadianProvinceReference } from '@/util/canadian-province-reference';
import { getEffectiveDeliveryLocations } from '@/util/delivery-location-display';

export default {
  name: 'EditJobLocationDialog',
  components: {
    FormMapSearchBox,
    BaseDialog,
    FormButton,
  },
  data() {
    return {
      value: null,
      opportunity: null,
      loading: false,
    };
  },
  computed: {
    singleTenderDelivery() {
      const tender = this.opportunity && this.opportunity.tender;
      if (!tender) return null;
      const deliveries = getEffectiveDeliveryLocations(tender);
      if (deliveries.length !== 1) return null;
      const delivery = deliveries[0];
      if (!delivery) return null;
      const precise = delivery.streetAddress || delivery.locality || delivery.postalCode;
      const singleArea = delivery.area && !/[\/|;\r\n]/.test(delivery.area);
      return precise || singleArea || isCanadianProvinceReference(delivery) ? delivery : null;
    },
    unresolvedJobLocation() {
      const location = this.opportunity && this.opportunity.jobLocation;
      return !location || location.longitude == null || location.latitude == null;
    },
    isFormInvalid() {
      return !this.value || this.value.longitude == null || this.value.latitude == null;
    },
    hasChanged() {
      return (
        this.value !== null &&
        (!this.opportunity.jobLocation || this.opportunity.jobLocation.addressText !== this.value.fullAddress)
      );
    },
  },
  methods: {
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('opportunityJobLocation', ['editOpportunityJobLocation', 'selectTenderDeliveryLocation']),
    openDialog(opportunity) {
      this.opportunity = opportunity;
      this.value = opportunity.jobLocation
        ? {
            fullAddress: opportunity.jobLocation.addressText,
            longitude:
              opportunity.jobLocation.longitude == null ? null : Number.parseFloat(opportunity.jobLocation.longitude),
            latitude:
              opportunity.jobLocation.latitude == null ? null : Number.parseFloat(opportunity.jobLocation.latitude),
          }
        : null;
      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.jobLocationInput.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.value = null;
      this.$refs.jobLocationInput.clear();
    },
    onSubmit() {
      this.loading = true;
      let body = {
        addressText: this.value.fullAddress,
        city: this.value.city,
        region: this.value.region,
        countryName: this.value.countryName,
        postalCode: this.value.postalCode,
        longitude: this.value.longitude,
        latitude: this.value.latitude,
      };
      return this.editOpportunityJobLocation({ opportunityId: this.opportunity.id, jobLocation: body })
        .then(async () => {
          await this.getOpportunity({ id: this.opportunity.id, silent: true });
          this.closeDialog();
        })
        .catch((error) => this.$toast.error(error))
        .finally(() => {
          this.loading = false;
        });
    },
    onUseTenderDelivery() {
      if (!this.singleTenderDelivery || this.loading) return;
      this.loading = true;
      return this.selectTenderDeliveryLocation({ opportunityId: this.opportunity.id })
        .then(async () => {
          await this.getOpportunity({ id: this.opportunity.id, silent: true });
          this.closeDialog();
        })
        .catch((error) =>
          this.$toast.error(
            error && error.response && error.response.status === 400
              ? this.$t('page.opportunity-details.opportunity-manipulations.tender-delivery-unresolved')
              : error.message || error,
          ),
        )
        .finally(() => {
          this.loading = false;
        });
    },
    handleKeyup(event) {
      const keyCode = event.keyCode || event.charCode;

      if (keyCode === 13) {
        if (!this.isFormInvalid && !this.loading) this.onSubmit();
        return;
      }
    },
    cancelClick() {
      this.closeDialog();
    },
  },
  created() {
    document.addEventListener('keyup', this.handleKeyup);
  },
  destroyed() {
    document.removeEventListener('keyup', this.handleKeyup);
  },
};
</script>

<style lang="scss">
.base-dialog__edit-job-location {
  height: auto;
  overflow: visible;
  width: 750px;
  position: relative;

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
    overflow: visible;
  }

  .close-icon__container {
    display: none;
  }
}

.edit-job-location {
  &__search-box {
    margin-bottom: 16px;
    position: relative;
    z-index: 1;
  }

  &__delivery-area {
    border-top: 1px solid $color-neutral-pale;
    padding-top: 16px;

    p {
      color: $color-normal;
      font-size: 14px;
      line-height: 1.4;
      margin: 8px 0 0;
    }
  }

  &__actions {
    margin-top: 24px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }
}
</style>
